import { useEffect, useRef, useSyncExternalStore, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getMaintenanceSnapshot,
  startMaintenanceFlagPolling,
  subscribeAuthRequired,
  subscribeMaintenance,
} from '@/lib/maintenance';
import { rememberReturnTo } from '@/auth/AuthProvider';
import { LoadingState } from '@/components/ui/states';

/**
 * The branded standalone page M1-29 asks for — same layout shape as `SignIn`
 * (full-viewport centered, no `StaffLayout` shell), since it has to work for
 * a visitor who is not signed in and for whom the shell's own data would not
 * load anyway.
 */
export function MaintenancePage({ message }: { message?: string }) {
  const { t } = useTranslation();

  return (
    <main className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center gap-4 px-6 text-center">
      <div>
        <h1 className="text-2xl font-semibold">{t('app.name')}</h1>
        <p className="mt-2 text-lg font-medium">{t('maintenance.title')}</p>
      </div>
      <p className="text-sm text-[var(--color-muted-foreground)]">
        {message || t('maintenance.description')}
      </p>
      {/* Announced, not just shown — a screen reader user gets the same "this
          is temporary and automatic" reassurance a sighted user reads visually. */}
      <LoadingState label={t('maintenance.checking')} className="justify-center py-0" />
    </main>
  );
}

/**
 * Wraps the whole app: renders the maintenance page in place of `children`
 * once `lib/maintenance.ts`'s store says `down`, and separately reacts to a
 * 401 by routing to sign-in — the two things `apiClient`'s middleware
 * reports, kept apart because a maintenance state must never eat a sign-out.
 *
 * Deliberately ABOVE `RequireAuth`/`QueryClientProvider`'s consumers in the
 * tree (see `App.tsx`) rather than nested under them: the AC requires this to
 * work for a signed-out visitor, and a gate that only mounted once
 * authenticated could never catch an outage during sign-in itself.
 */
export function MaintenanceGate({ children }: { children: ReactNode }) {
  const state = useSyncExternalStore(subscribeMaintenance, getMaintenanceSnapshot);
  const location = useLocation();
  const queryClient = useQueryClient();
  const { instance } = useMsal();

  useEffect(() => startMaintenanceFlagPolling(), []);

  useEffect(
    () =>
      subscribeAuthRequired(() => {
        if (location.pathname !== '/signin') {
          rememberReturnTo(location.pathname + location.search);
          // A REAL navigation, not react-router's `navigate`: MSAL's own
          // auth-cache state (`useIsAuthenticated()`, what `SignIn` checks)
          // is derived by `MsalProvider` from account-change EVENTS, which
          // `instance.clearCache()` — local-only, no redirect — never fires;
          // only a fresh page load re-derives it, from the now-cleared
          // cache. A 401 does not otherwise touch that cache, so without
          // this, `SignIn` reads `isAuthenticated` as still true and
          // immediately bounces straight back — a redirect loop against a
          // still-401ing request.
          instance
            .clearCache()
            .catch(() => {
              // A failed cache clear must not strand the user on the
              // 401'ing screen — still navigate to sign-in below.
            })
            .finally(() => {
              window.location.assign('/signin');
            });
        }
      }),
    [location, instance],
  );

  // Recovery: once the outage clears, refetch whatever is mounted rather
  // than leaving screens sitting on the error they failed with — "returns to
  // the app... with no hard refresh" (AC) means the DATA comes back too, not
  // just the chrome. Scoped to what actually needs it (an error left over
  // from the outage, or anything that has gone stale since) rather than
  // every active query app-wide — an unscoped `invalidateQueries()` would
  // also refetch always-mounted chrome queries that never even failed, all
  // at once, right as the API is cold-restarting.
  const wasDown = useRef(false);
  useEffect(() => {
    if (wasDown.current && state.status === 'ok') {
      void queryClient.invalidateQueries({
        predicate: (query) => query.state.status === 'error' || query.isStale(),
      });
    }
    wasDown.current = state.status === 'down';
  }, [state.status, queryClient]);

  return (
    <>
      {/* Kept mounted, never unmounted, under the maintenance overlay — a
          brief outage must not discard local-only state (an in-progress job
          note, say) the way swapping this subtree out for `MaintenancePage`
          at the same position would. `inert` removes it from the
          accessibility tree and keyboard/pointer interaction while covered. */}
      <div inert={state.status === 'down' ? true : undefined}>{children}</div>
      {state.status === 'down' ? (
        <div className="fixed inset-0 z-50 bg-[var(--color-background)]">
          <MaintenancePage message={state.message} />
        </div>
      ) : null}
    </>
  );
}
